// A sawmill: a timber bench with a big circular saw blade and a log being sawn
// into planks, under an open shelter roof. Ported 1:1 from age-of-mcp's
// sawmill.ts: the shared foundation/scaffold come from the library, the bench,
// blade, log and shelter are local helpers, and the four build stages are
// transcribed from stage(s).
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, benchWork, spin, EFFECT_LOOP, type Part } from "../../lib";

const WOOD = "#8a5a2c";
const FRAME = "#6b4523";
const METAL = "#b8bdc4";
const LOG = "#7a5526";
const ROOF = "#5b3d22";

// The work bench the saw and log sit on.
function bench(): Part {
  return { geometry: new THREE.BoxGeometry(0.7, 0.18, 0.5), color: WOOD, position: [0, 0.23, 0] };
}

// A big circular saw blade standing upright on the bench (axle along z). Named
// so the "active" animation can spin it about its hub.
function sawBlade(): Part {
  return {
    geometry: new THREE.CylinderGeometry(0.24, 0.24, 0.04, 16),
    color: METAL,
    position: [0.06, 0.5, 0],
    rotation: [Math.PI / 2, 0, 0],
    name: "blade",
  };
}

// A log lying across the bench, being sawn into planks (axle along x).
function log(): Part {
  return {
    geometry: new THREE.CylinderGeometry(0.08, 0.08, 0.5, 8),
    color: LOG,
    position: [-0.08, 0.36, 0],
    rotation: [0, 0, Math.PI / 2],
  };
}

// An open shelter: four corner posts under a flat plank roof.
function shelter(): Part[] {
  const parts: Part[] = [];
  for (const x of [-0.32, 0.32]) {
    for (const z of [-0.32, 0.32]) {
      parts.push({
        geometry: new THREE.CylinderGeometry(0.035, 0.035, 0.55, 6),
        color: FRAME,
        position: [x, 0.14 + 0.275, z],
      });
    }
  }
  parts.push({
    geometry: new THREE.BoxGeometry(0.92, 0.06, 0.92),
    color: ROOF,
    position: [0, 0.14 + 0.58, 0],
  });
  return parts;
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.86)];
  if (s === 1) return [foundationSlab(0.86), bench(), ...scaffold(0.84, 0.6)];
  if (s === 2) return [foundationSlab(0.86), bench(), sawBlade(), ...scaffold(0.84, 1.05)];
  return [foundationSlab(0.86), bench(), sawBlade(), log(), ...shelter()];
}

export const sawmill = defineModel({
  id: "sawmill",
  tiles: { w: 2, h: 2 },
  batchId: "standard",
  name: "Sawmill",
  tags: ["building", "sawmill", "low-poly", "age-of-mcp", "classic", "animated"],
  summary: "Low-poly sawmill.",
  description: "A timber saw bench that saws raw wood into planks.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [
    {
      id: "active",
      label: "Saw spinning",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [spin("blade", [0.06, 0.5, 0], { turns: 4 })],
    },
  ],
  // Worker choreography, ported 1:1 from age-of-mcp's sawmill: two bench spots
  // (front and back of the saw) where a sawyer works the bench-work program.
  stations: [
    { id: "bench-front", position: [0, 0.14, 0.42], facing: Math.PI, description: "Sawyer's spot, front of the saw." },
    { id: "bench-back", position: [0, 0.14, -0.42], facing: 0, description: "Sawyer's spot, back of the saw." },
  ],
  interactions: [
    { id: "saw", label: "Sawing", station: "bench-front", variants: ["complete"], ...benchWork() },
  ],
});
